import type { ReactNode } from 'react';
import { Wifi, WifiOff } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * La banda che prende il posto delle righe torneo e informazioni quando qualcuno non e' collegato (reference 07/10, punto 6:
 * «avversario disconnesso» in avviso, «mia connessione persa» in rosso). E' il componente `Grazia` del canvas v8, SEPARATO dal
 * tavolo: con R10 ci va il conto alla rovescia e la barra del residuo (quando il game host manda il campo, in una versione
 * nuova della board-ui — L-2927 (b), cantiere `qchess-orcr-4-grazia`).
 *
 * 🔴 Il conto e la barra del residuo vengono SOLO dal campo `SeatPayload.grace` del game host (board-ui 0.2.0, §5.1): `conto` e
 * `residuo` sono `null` se il campo manca o la grazia NON e' armata (`running: false`: un posto vuoto prima del primo join o dopo
 * un riavvio) — un tempo inventato qui sarebbe un'affermazione falsa sul tempo che resta. La banda cresce col testo (nessuna
 * altezza fissa: nella resa del runtime minimo la barra del residuo non si vedeva, e DEVE vedersi).
 */
export type TonoBanda = 'avviso' | 'errore' | 'ok';

export function BandaConnessione({
  tono,
  titolo,
  conto = null,
  residuo = null,
  testo,
  azioni,
  dati,
}: {
  tono: TonoBanda;
  titolo: string;
  /** Il conto alla rovescia `m:ss`, a destra del titolo; `null` = nessuno. */
  conto?: string | null;
  /** Quanto resta della grazia, 0..1, per la barra; `null` = nessuna barra. */
  residuo?: number | null;
  /** Una o due righe di spiegazione; `null` = niente da aggiungere. */
  testo: string | null;
  azioni?: ReactNode;
  dati: string;
}) {
  return (
    <div
      role="status"
      data-testid="chess-banda"
      data-banda={dati}
      className={cn(
        'flex flex-col gap-1.5 rounded-xl border px-3 py-2.5',
        tono === 'errore' ? 'border-danger/45 bg-danger/10' : tono === 'ok' ? 'border-brand/45 bg-brand/10' : 'border-warning/45 bg-warning/10',
      )}
    >
      <div className="flex items-center gap-2">
        {tono === 'ok' ? (
          <Wifi size={16} aria-hidden className="text-brand" />
        ) : (
          <WifiOff size={16} aria-hidden className={tono === 'errore' ? 'text-[#ff6b66]' : 'text-warning'} />
        )}
        <span className={cn('min-w-0 flex-1 truncate text-[13px] font-bold', tono === 'errore' ? 'text-[#ff6b66]' : tono === 'ok' ? 'text-brand' : 'text-warning')}>
          {titolo}
        </span>
        {conto !== null && (
          <span
            data-testid="chess-banda-conto"
            className={cn('text-lg font-extrabold tabular-nums', tono === 'errore' ? 'text-[#ff6b66]' : 'text-warning')}
          >
            {conto}
          </span>
        )}
      </div>
      {testo !== null && <p className="text-xs leading-snug text-fg">{testo}</p>}
      {residuo !== null && (
        <div className="h-1 overflow-hidden rounded-full bg-fg/10" data-testid="chess-banda-residuo" data-residuo={residuo.toFixed(3)}>
          <div
            className={cn('h-1 rounded-full', tono === 'errore' ? 'bg-[#ff6b66]' : 'bg-warning')}
            style={{ width: `${Math.round(Math.max(0, Math.min(1, residuo)) * 100)}%` }}
          />
        </div>
      )}
      {azioni}
    </div>
  );
}
